import { isPlainObject } from 'is-plain-object';

import { Set as ISet, memoizeClassMethods, Record } from '@gitbook/util-immutable';

import { isType, MODEL_TYPES } from '../constants';
import { Data } from './data';

export interface MarkProperties {
    data: Data;
    type: string;
}

export type MarkCreateAttrs =
    | string
    | Mark
    | Partial<Omit<MarkProperties, 'data'> & { data: object | Data }>;

export type MarkCreateSetProps = ISet<Mark> | MarkCreateAttrs[];

/*
 * Mark.
 */
export class Mark extends Record<MarkProperties>({
    data: Data.create(),
    type: '',
}) {
    public declare readonly type: string;
    public declare readonly data: Data;

    /*
     * Object.
     */
    get object(): 'mark' {
        return 'mark';
    }

    /*
     * Check if `any` is a `Mark`.
     */

    public static isMark(input: any): input is Mark {
        return isType('MARK', input);
    }

    /*
     * Create a new `Mark` with `attrs`.
     */
    public static create(attrs: MarkCreateAttrs = {}): Mark {
        if (Mark.isMark(attrs)) {
            return attrs;
        }

        if (typeof attrs === 'string') {
            attrs = { type: attrs };
        }

        return Mark.createRecord({
            ...attrs,
            data: Data.create(attrs.data || {}),
        });
    }

    /*
     * Create a set of marks.
     */
    public static createSet(elements?: MarkCreateSetProps): ISet<Mark> {
        if (!elements) {
            return ISet.create();
        }

        if (ISet.isSet(elements)) {
            return elements;
        }

        if (Array.isArray(elements)) {
            return ISet.create(elements.map((element) => Mark.create(element)));
        }

        throw new Error(
            `\`Mark.createSet\` only accepts sets, arrays or null, but you passed it: ${elements}`
        );
    }

    /**
     * Create a dictionary of settable mark properties from `attrs`.
     */
    public static createProperties(attrs: MarkCreateAttrs | Mark = {}): MarkCreateAttrs {
        if (Mark.isMark(attrs)) {
            return {
                data: attrs.data,
                type: attrs.type,
            };
        }

        if (typeof attrs === 'string') {
            return { type: attrs };
        }

        if (isPlainObject(attrs)) {
            const props: Partial<MarkProperties> = {};
            if ('type' in attrs) {
                props.type = attrs.type;
            }
            if ('data' in attrs) {
                props.data = Data.create(attrs.data);
            }
            return props;
        }

        throw new Error(
            `\`Mark.createProperties\` only accepts objects, strings or marks, but you passed it: ${attrs}`
        );
    }

    /*
     * Check if `any` is a set of marks.
     */
    public static isMarkSet(input: any): input is ISet<Mark> {
        return ISet.isSet(input) && input.every((item) => Mark.isMark(item));
    }

    /*
     * Get the component for the node from a `schema`.
     *
     * @param {Schema} schema
     * @return {Component|Void}
     */

    public getComponent(schema) {
        return schema.__getComponent(this);
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Mark.prototype[MODEL_TYPES.MARK] = true;

/*
 * Memoize read methods.
 */

memoizeClassMethods(Mark, ['getComponent']);
